import React, { useState, useEffect } from 'react';
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { reservationApi } from '../../services/reservationApi';
import { ReservationCalendar as ReservationCalendarType } from '../../types/reservation';
import { formatDateKST } from '../../utils/dateUtils';

interface ReservationCalendarProps {
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  onSeatSelect?: (seatId: string) => void;
}

export const ReservationCalendar: React.FC<ReservationCalendarProps> = ({
  selectedDate,
  onDateSelect,
  onSeatSelect,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date(selectedDate));
  const [calendarData, setCalendarData] =
    useState<ReservationCalendarType | null>(null);
  const [loading, setLoading] = useState(false);

  // 달력 데이터 로드
  const loadCalendarData = async (date: Date) => {
    if (!date) return;

    setLoading(true);
    try {
      const dateString = formatDateKST(date);
      const data = await reservationApi.getReservationCalendar(dateString);
      setCalendarData(data);
    } catch (error) {
      console.error('달력 데이터 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCalendarData(selectedDate);
  }, [selectedDate]);

  // 달 변경
  const changeMonth = (direction: 'prev' | 'next') => {
    const newMonth = new Date(currentMonth);
    if (direction === 'prev') {
      newMonth.setMonth(newMonth.getMonth() - 1);
    } else {
      newMonth.setMonth(newMonth.getMonth() + 1);
    }
    setCurrentMonth(newMonth);
  };

  // 달력 날짜 생성
  const generateCalendarDays = () => {
    const startOfMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      1
    );
    const endOfMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      0
    );
    const startDate = new Date(startOfMonth);
    startDate.setDate(startDate.getDate() - startOfMonth.getDay());

    const days = [];
    const today = new Date();

    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);

      const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
      const isToday = date.toDateString() === today.toDateString();
      const isSelected = date.toDateString() === selectedDate.toDateString();
      const isPast = date < today && !isToday;

      days.push({
        date,
        isCurrentMonth,
        isToday,
        isSelected,
        isPast,
        dayNumber: date.getDate(),
      });
    }

    return days;
  };

  const calendarDays = generateCalendarDays();
  const monthNames = [
    '1월',
    '2월',
    '3월',
    '4월',
    '5월',
    '6월',
    '7월',
    '8월',
    '9월',
    '10월',
    '11월',
    '12월',
  ];

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <CalendarIcon className="h-5 w-5 mr-2 text-blue-600" />
          예약 달력
        </h3>

        <div className="flex items-center space-x-4">
          <button
            onClick={() => changeMonth('prev')}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>

          <span className="font-medium text-gray-900">
            {currentMonth.getFullYear()}년 {monthNames[currentMonth.getMonth()]}
          </span>

          <button
            onClick={() => changeMonth('next')}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* 요일 헤더 */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['일', '월', '화', '수', '목', '금', '토'].map((day) => (
          <div
            key={day}
            className="p-2 text-center text-sm font-medium text-gray-500"
          >
            {day}
          </div>
        ))}
      </div>

      {/* 달력 날짜 */}
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((day, index) => {
          const handleDateClick = () => {
            if (!day.isPast && day.isCurrentMonth) {
              onDateSelect(day.date);
              loadCalendarData(day.date);
            }
          };

          return (
            <button
              key={index}
              onClick={handleDateClick}
              disabled={day.isPast}
              className={`
                p-2 h-10 text-sm rounded-lg transition-colors
                ${day.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}
                ${day.isToday ? 'bg-blue-100 text-blue-600 font-semibold' : ''}
                ${day.isSelected ? 'bg-blue-600 text-white' : ''}
                ${
                  day.isPast
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'hover:bg-gray-100'
                }
                ${
                  !day.isCurrentMonth || day.isPast
                    ? 'cursor-not-allowed'
                    : 'cursor-pointer'
                }
              `}
            >
              {day.dayNumber}
            </button>
          );
        })}
      </div>

      {/* 선택된 날짜 정보 */}
      {calendarData && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-3">
            {selectedDate.toLocaleDateString('ko-KR')} 예약 현황
          </h4>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">전체 좌석:</span>
              <span className="ml-2 font-medium">
                {calendarData.total_seats}석
              </span>
            </div>
            <div>
              <span className="text-gray-600">예약 가능:</span>
              <span className="ml-2 font-medium text-green-600">
                {calendarData.available_count}석
              </span>
            </div>
          </div>

          {calendarData.reserved_seats.length > 0 && (
            <div className="mt-4">
              <h5 className="font-medium text-gray-900 mb-2">예약된 좌석</h5>
              <div className="space-y-2">
                {calendarData.reserved_seats
                  .slice(0, 3)
                  .map((reservation, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between text-xs"
                    >
                      <span className="text-gray-600">
                        {reservation.seat_number}번 좌석
                      </span>
                      <span className="text-gray-500">
                        {new Date(reservation.start_time).toLocaleTimeString(
                          'ko-KR',
                          {
                            hour: '2-digit',
                            minute: '2-digit',
                          }
                        )}{' '}
                        -
                        {new Date(reservation.end_time).toLocaleTimeString(
                          'ko-KR',
                          {
                            hour: '2-digit',
                            minute: '2-digit',
                          }
                        )}
                      </span>
                    </div>
                  ))}
                {calendarData.reserved_seats.length > 3 && (
                  <div className="text-xs text-gray-500 text-center pt-2">
                    외 {calendarData.reserved_seats.length - 3}건...
                  </div>
                )}
              </div>
            </div>
          )}

          {loading && (
            <div className="text-center text-gray-500 text-sm mt-2">
              데이터 로딩 중...
            </div>
          )}
        </div>
      )}
    </div>
  );
};
